import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Users, BookOpen, GraduationCap, Layers, Ticket, Mail, TrendingUp, ArrowRight } from 'lucide-react';

const CARDS = [
  { key: 'users', label: 'Users', icon: Users, to: '/admin/users', color: 'from-purple-500 to-purple-700' },
  { key: 'courses', label: 'Courses', icon: BookOpen, to: '/admin/courses', color: 'from-orange-500 to-red-600' },
  { key: 'enrollments', label: 'Enrollments', icon: GraduationCap, to: '/admin/enrollments', color: 'from-blue-500 to-cyan-600' },
  { key: 'categories', label: 'Categories', icon: Layers, to: '/admin/categories', color: 'from-emerald-500 to-teal-600' },
  { key: 'vouchers', label: 'Vouchers', icon: Ticket, to: '/admin/vouchers', color: 'from-amber-500 to-orange-600' },
  { key: 'contact', label: 'Enquiries', icon: Mail, to: '/admin/contact', color: 'from-pink-500 to-rose-600' },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.admin.stats().then(setStats).catch(() => {}); }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold">Dashboard overview</h1>
        <p className="text-gray-500 text-sm mt-1">Manage your entire Skillbridge.guru platform in one place.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {CARDS.map(c => (
          <Link key={c.key} to={c.to} className="group">
            <div className={`p-5 rounded-2xl bg-gradient-to-br ${c.color} text-white shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all`}>
              <div className="flex items-start justify-between mb-6">
                <c.icon size={24} />
                <ArrowRight size={16} className="opacity-70 group-hover:translate-x-1 transition-transform" />
              </div>
              <div className="text-3xl font-extrabold">{stats[c.key] ?? 0}</div>
              <div className="text-xs uppercase tracking-wider opacity-90 mt-1">{c.label}</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">Quick actions</h3>
            <TrendingUp size={16} className="text-emerald-500" />
          </div>
          <div className="space-y-2">
            <Link to="/admin/courses" className="flex items-center justify-between p-3 rounded-lg hover:bg-purple-50 border border-gray-100 hover:border-purple-200">
              <span className="font-semibold text-sm">Add a new course</span><ArrowRight size={14} />
            </Link>
            <Link to="/admin/vouchers" className="flex items-center justify-between p-3 rounded-lg hover:bg-purple-50 border border-gray-100 hover:border-purple-200">
              <span className="font-semibold text-sm">Create a discount voucher</span><ArrowRight size={14} />
            </Link>
            <Link to="/admin/settings" className="flex items-center justify-between p-3 rounded-lg hover:bg-purple-50 border border-gray-100 hover:border-purple-200">
              <span className="font-semibold text-sm">Update site branding</span><ArrowRight size={14} />
            </Link>
            <Link to="/admin/admins" className="flex items-center justify-between p-3 rounded-lg hover:bg-purple-50 border border-gray-100 hover:border-purple-200">
              <span className="font-semibold text-sm">Invite an admin</span><ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#6b47dc] to-[#ff5722] p-6 rounded-2xl text-white">
          <h3 className="font-bold text-lg mb-1">Welcome to Admin Panel</h3>
          <p className="text-sm opacity-90 mb-4">You have full control over courses, categories, vouchers, users and site settings.</p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-white/10 rounded-lg p-3"><div className="opacity-80">Users</div><div className="font-extrabold text-xl">{stats.users || 0}</div></div>
            <div className="bg-white/10 rounded-lg p-3"><div className="opacity-80">Enrollments</div><div className="font-extrabold text-xl">{stats.enrollments || 0}</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
